import { useState, type FC } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import useTranslation from '@/hooks/useTranslation'
import type { GridFilterProps } from '@/components/grid/types'
import {
  STATION_STATE_BUCKETS,
  STATION_STATE_BUCKET_LABEL,
  stationStateBucket,
  type StationStateBucket,
} from './station-state-buckets'

export interface StateBucketModel {
  buckets: StationStateBucket[]
}

export const isStateBucketModel = (model: unknown): model is StateBucketModel =>
  Boolean(
    model &&
      typeof model === 'object' &&
      Array.isArray((model as StateBucketModel).buckets) &&
      (model as StateBucketModel).buckets.length > 0,
  )

/** Does the row's state fall in one of the picked groups? */
export const matchesStateBuckets = (state: unknown, model: unknown): boolean =>
  !isStateBucketModel(model) ||
  model.buckets.includes(stationStateBucket(state))

/**
 * Status filter, offering the four groups the summary counts rather than the
 * nine states a station can hold — see `station-state-buckets.ts`.
 *
 * Not a plain value set: a set filter matches the cell's own value, and the
 * cell has to keep showing the exact state ("Wait Start"), not the group it
 * falls in. So the grid carries this model and the page applies it, the same
 * way it does the "contains" filters.
 */
const StateBucketFilter: FC<GridFilterProps<any>> = ({
  model,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<StationStateBucket[]>(
    isStateBucketModel(model) ? model.buckets : [],
  )

  const toggle = (bucket: StationStateBucket) =>
    setDraft((previous) =>
      previous.includes(bucket)
        ? previous.filter((item) => item !== bucket)
        : [...previous, bucket],
    )

  return (
    <Box sx={{ padding: '8px', minWidth: 220 }}>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          color: 'grey.600',
          px: '8px',
          pb: '4px',
        }}
      >
        {t('stationsColumnStatus')}
      </Typography>

      <Stack>
        {STATION_STATE_BUCKETS.map((bucket) => (
          <FormControlLabel
            key={bucket}
            control={
              <Checkbox
                size="small"
                checked={draft.includes(bucket)}
                onChange={() => toggle(bucket)}
              />
            }
            label={t(STATION_STATE_BUCKET_LABEL[bucket])}
            sx={{
              margin: 0,
              px: '4px',
              '& .MuiFormControlLabel-label': {
                fontSize: '13px',
                color: 'text.main',
              },
            }}
          />
        ))}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 1 }}
      >
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            setDraft([])
            onChange(null)
            onClose()
          }}
        >
          {t('commonActionsReset')}
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            // An empty pick means "no filter", not "match nothing" — the same
            // rule the grid's own set filter follows.
            onChange(draft.length > 0 ? { buckets: draft } : null)
            onClose()
          }}
        >
          {t('commonActionsApply')}
        </Button>
      </Stack>
    </Box>
  )
}

export default StateBucketFilter
